/* ==========================================================================
   Consent-aware storefront funnel events.

   Components publish semantic `ev:commerce` DOM events. This module is the
   only place that decides whether they may leave the page and which fields are
   safe to expose to GA4/Clarity. Shopify's Google & YouTube app remains the
   source of truth for checkout_completed / purchase events.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__evCommerceAnalyticsBound) return;
  window.__evCommerceAnalyticsBound = true;

  const EVENT_NAMES = new Set([
    'product_loaded',
    'size_guide_opened',
    'size_selected',
    'add_to_cart_attempt',
    'add_to_cart_success',
    'cart_opened',
    'checkout_requested',
    'accelerated_checkout_clicked',
    'coupon_applied',
  ]);
  const SAFE_FIELDS = new Set([
    'product_id',
    'variant_id',
    'size',
    'quantity',
    'source',
    'verification_status',
    'value',
    'currency',
    'item_count',
  ]);
  const CLARITY_FIELDS = {
    product_id: 'ev_product_id',
    variant_id: 'ev_variant_id',
    size: 'ev_size',
    source: 'ev_source',
    verification_status: 'ev_size_guide_status',
  };
  const INTERNAL_SESSION_KEY = 'ev:analytics-internal:v1';

  function isInternalContext() {
    try { if (window.sessionStorage.getItem(INTERNAL_SESSION_KEY) === 'true') return true; }
    catch (_) { /* URL and design-mode checks work without storage. */ }
    if (window.Shopify && window.Shopify.designMode) return true;
    // Cookie-selected previews can open in a fresh tab without query params
    // or sessionStorage. The rendered theme still identifies its own role.
    const themeRole = window.Shopify?.theme?.role;
    if (themeRole && themeRole !== 'main') return true;

    const host = window.location && window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return true;

    const params = new URLSearchParams(window.location.search || '');
    const internalParams = [
      'preview_theme_id',
      'previewPath',
      'shopify_preview',
      'shopify-debug',
      'verify',
      'gtm_debug',
      'gtm_preview',
      'pb',
      'codex_p1_preview',
    ];
    if (internalParams.some((name) => params.has(name))) return true;

    try {
      const referrer = new URL(document.referrer || window.location.href);
      if (referrer.hostname === 'tagassistant.google.com') return true;
    } catch (error) {
      /* An invalid referrer is not evidence of an internal visit. */
    }
    return false;
  }

  function analyticsAllowed() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
      try { return privacy.analyticsProcessingAllowed() === true; }
      catch (error) { return false; }
    }
    return false;
  }

  function safePayload(payload) {
    const safe = {};
    if (!payload || typeof payload !== 'object') return safe;
    Object.keys(payload).forEach((key) => {
      if (!SAFE_FIELDS.has(key)) return;
      const value = payload[key];
      if (value === null || value === undefined || value === '') return;
      if (typeof value === 'string' && value.length <= 128) safe[key] = value;
      if (typeof value === 'number' && Number.isFinite(value)) safe[key] = value;
      if (typeof value === 'boolean') safe[key] = value;
    });
    return safe;
  }

  const internal = isInternalContext();
  if (internal) {
    try { window.sessionStorage.setItem(INTERNAL_SESSION_KEY, 'true'); } catch (_) {}
    document.documentElement.setAttribute('data-ev-internal-session', 'true');
    try {
      if (analyticsAllowed() && typeof window.clarity === 'function') {
        window.clarity('set', 'ev_traffic_type', 'internal');
      }
    } catch (_) { /* Diagnostics cannot interrupt the storefront. */ }
  }

  function track(name, payload) {
    if (!EVENT_NAMES.has(name) || internal || !analyticsAllowed()) return false;
    const safe = safePayload(payload);

    const shopifyAnalytics = window.Shopify && window.Shopify.analytics;
    if (shopifyAnalytics && typeof shopifyAnalytics.publish === 'function') {
      try {
        const published = shopifyAnalytics.publish(`essentials_vault:${name}`, safe);
        if (published && typeof published.catch === 'function') published.catch(() => {});
      } catch (error) {
        /* A pixel bridge failure must never interrupt the storefront action. */
      }
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: `ev_${name}` }, safe));
    } catch (_) { /* A provider failure must not block other destinations. */ }

    try {
      if (typeof window.clarity === 'function') {
        Object.keys(CLARITY_FIELDS).forEach((key) => {
          if (safe[key] !== undefined) {
            window.clarity('set', CLARITY_FIELDS[key], String(safe[key]));
          }
        });
        window.clarity('event', `ev_${name}`);
      }
    } catch (_) { /* The existing Clarity app owns transport and recording. */ }
    return true;
  }

  window.EssentialsVault = window.EssentialsVault || {};
  window.EssentialsVault.analytics = {
    track,
    analyticsAllowed,
    isInternalContext: () => internal,
  };

  document.addEventListener('ev:commerce', (event) => {
    const detail = event.detail || {};
    track(detail.name, detail.payload);
  });

  // Native wallets use closed shadow DOM and may prevent the click's default
  // action themselves. Observe the composed click without interfering with it;
  // it proves an interaction, never a started checkout or a completed payment.
  document.addEventListener('click', (event) => {
    const payment = event.target?.closest?.('[data-native-payment]');
    if (!payment || payment.hidden || payment.hasAttribute('inert')) return;
    const nativeButton = event.target.closest('shopify-accelerated-checkout, .shopify-payment-button__button');
    if (!nativeButton || !payment.contains(nativeButton)) return;
    const picker = payment.closest('.pdp variant-picker');
    const input = payment.closest('form')?.querySelector('input[name="id"]');
    if (!picker || !input || input.disabled || !input.value) return;
    try {
      const product = JSON.parse(picker.querySelector('[data-product-json]')?.textContent);
      const variant = product?.variants?.find(item => String(item.id) === input.value);
      if (!variant?.available) return;
      track('accelerated_checkout_clicked', {
        product_id: String(product.id), variant_id: input.value, source: 'product_payment_button',
      });
    } catch (_) { /* Invalid component data must never block native payment. */ }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || form.tagName !== 'FORM' || event.submitter?.name !== 'checkout') return;
    const destination = new URL(form.action, window.location.href);
    if (destination.origin !== window.location.origin || !/\/cart\/?$/.test(destination.pathname)) return;
    // A browser-dispatched submit may run a microtask checkpoint between
    // listeners. Wait for the whole dispatch, including later cancellation.
    window.setTimeout(() => {
      if (!event.defaultPrevented) track('checkout_requested', { source: 'cart_form' });
    }, 0);
  });

  document.addEventListener('ev:discount-applied', () => track('coupon_applied', { source: 'cart_response' }));

  let recordedPicker = null;
  let recordedProductId = null;
  function productLoaded(event) {
    const picker = document.querySelector('.pdp variant-picker[data-product-id]');
    if (!picker || picker === recordedPicker) return;
    let product;
    try { product = JSON.parse(picker.querySelector('[data-product-json]')?.textContent || 'null'); }
    catch (_) { return; }
    const id = picker.dataset.productId;
    if (!/^\d+$/.test(id) || String(product?.id) !== id || !Array.isArray(product.variants)) return;
    const handle = window.location.pathname.match(/\/products\/([^/]+)\/?$/)?.[1];
    if (!handle || handle !== product.handle) return;
    if (event?.type === 'pdp:color-change' && String(event.detail?.productId) !== id) return;
    if (event?.type === 'pdp:color-change' && recordedProductId === id) {
      recordedPicker = picker;
      return;
    }
    const input = picker.querySelector('[name="id"]');
    const variant = input && !input.disabled && product.variants.find(item => String(item.id) === input.value);
    if (track('product_loaded', { product_id: id, variant_id: variant ? String(variant.id) : undefined,
      source: event?.type === 'pdp:color-change' ? 'color_navigation' : 'product_page' })) {
      recordedPicker = picker;
      recordedProductId = id;
    }
  }

  // A page still being viewed can be recorded after consent becomes available.
  // Cart/checkout events that happened without consent are never queued or replayed.
  document.addEventListener('pdp:color-change', productLoaded);
  document.addEventListener('visitorConsentCollected', productLoaded);
  document.addEventListener('ev:consent-updated', productLoaded);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', productLoaded, { once: true });
  else productLoaded();
  try {
    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => {
      if (!error) productLoaded();
    });
  } catch (_) { /* The other readiness/consent signals remain available. */ }
})();
